// Pure decisions behind the ACTIVE screen's row anatomy (spec-active.md).
// Everything the screen has to WORK OUT lives here rather than in the
// component, for the reason `alarmRedesign` gives: `cloud/web`'s runner has no
// DOM, so a decision reachable only by mounting a component is a decision that
// never gets a test.
//
// The honesty rules this module carries:
//
//   - The 40px hero figure is for a PRESENT-TENSE reading only. `liveValueLine`
//     already says "Now 12.6 V" solely inside the fresh band and "12.4 V, 3 hr
//     ago" outside it; blowing the stale form up to 40px with a small age tag
//     beside it would read as live. So {@link readHeroLine} recognises exactly
//     the "Now …" shape and hands every other wording back whole, to be
//     rendered at body size.
//   - It SPLITS the composed line, never rebuilds it. The pieces concatenate
//     back to the original character for character — `formatThreshold` spaces
//     units per unit ("12.6 V" but "95°C"), so a reconstruction would quietly
//     invent a space that the configured row does not have, and the two bands
//     would stop agreeing about the same alarm. That is also what makes the
//     split unit-agnostic under ADR 0189: `liveValueLine` has already converted
//     both halves into the account's units, and {@link readHeroLine} matches
//     only the leading NUMBER, handing back whatever label followed it —
//     ` ft`, ` ftm`, `°F`, ` inHg` — untouched.
//   - The elapsed hero is time since the LAST STATE CHANGE, which is not "how
//     long it has been firing" (an Active→Silenced→Active alarm resets it,
//     data-mapping Screen-1 caveat). The caption words say so; this module only
//     formats the number.
//   - A clear phrase is derived ONLY from a `clearpoint` that actually came
//     over the wire. Recipe clear DELAYS are not in the §5.7 projection, so
//     "for 1 min" is never appended — that is the NO BACKING DATA line.

import type { UnitSettings } from '@neomaris/protocol';

import { distinctDecimals, formatThreshold } from './alarmSentence';
import { convertQuantity, type UnitCategory } from './displayUnits';
import type { AlarmWireTrigger } from './alarmDisplay';
import type { SeriesPoint } from './alarmRedesign';

// ---------------------------------------------------------------------------
// The hero figure, read back out of the composed value line.
// ---------------------------------------------------------------------------

/**
 * The value line broken into the pieces the hero cell styles differently.
 *
 * `kind: 'now'` is the only one that earns the 40px treatment. Its four parts
 * concatenate back to the original line exactly — the row's `data-testid`
 * element wraps all of them, so the text an e2e reads is still the whole
 * sentence both bands share.
 */
export type HeroLine =
  | {
      readonly kind: 'now';
      /** `'Now '`, kept verbatim so the join is lossless. */
      readonly lead: string;
      /** The number alone — the part that goes big. */
      readonly value: string;
      /** Whatever followed it up to the separator: `' V'`, `'°C'`, or `''`. */
      readonly unit: string;
      /** `' · alarms below 11.8 V'`, or `null` when the line had no phrase. */
      readonly phrase: string | null;
    }
  | {
      /** Any other wording — stale, last-transition, structured, or "No reading
       *  yet". Rendered whole, at reading size, exactly as composed. */
      readonly kind: 'text';
      readonly text: string;
    };

/** Matches only the present-tense form, and only up to the number. */
const NOW_VALUE = /^Now (-?\d+(?:\.\d+)?)/;

/** The separator `liveValueLine` joins its two halves with. */
const PHRASE_SEPARATOR = ' · ';

/**
 * Split a value line for the hero cell. See {@link HeroLine} — a line this
 * does not recognise comes back as `text`, never as a guess at its parts.
 */
export function readHeroLine(line: string): HeroLine {
  const match = NOW_VALUE.exec(line);
  const value = match?.[1];
  if (match === null || value === undefined) return { kind: 'text', text: line };
  const lead = line.slice(0, match[0].length - value.length);
  const rest = line.slice(match[0].length);
  const at = rest.indexOf(PHRASE_SEPARATOR);
  return {
    kind: 'now',
    lead,
    value,
    unit: at === -1 ? rest : rest.slice(0, at),
    phrase: at === -1 ? null : rest.slice(at),
  };
}

// ---------------------------------------------------------------------------
// Elapsed time and wall clock.
// ---------------------------------------------------------------------------

/** A big figure and its small inline unit — `14` + `min`. */
export interface ElapsedFigure {
  readonly value: string;
  readonly unit: string;
}

/**
 * How long ago `iso` was, as a figure and a unit.
 *
 * `null` for an unparseable stamp: the hero then renders nothing rather than a
 * zero, which would read as "just now". A stamp marginally in the future is
 * clamped to zero for the reason `formatRelativeShort` clamps — a boat clock a
 * second ahead of the viewer's is not a negative age.
 */
export function formatElapsed(iso: string | null | undefined, nowMs: number): ElapsedFigure | null {
  if (iso === null || iso === undefined) return null;
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return null;
  const sec = Math.floor(Math.max(0, nowMs - at) / 1000);
  if (sec < 60) return { value: String(sec), unit: 's' };
  const min = Math.floor(sec / 60);
  if (min < 60) return { value: String(min), unit: 'min' };
  const hr = Math.floor(min / 60);
  if (hr < 24) return { value: String(hr), unit: 'hr' };
  return { value: String(Math.floor(hr / 24)), unit: 'd' };
}

/**
 * `03:12` — the wall-clock time of a transition, in the READER's timezone,
 * 24-hour so it never needs an am/pm suffix in a caption. `null` when the stamp
 * will not parse.
 */
export function formatClockTime(iso: string | null | undefined): string | null {
  if (iso === null || iso === undefined) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  const at = new Date(ms);
  return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// The 24 h event-tick strip.
// ---------------------------------------------------------------------------

/**
 * Place each past firing across a right-anchored window, as a percentage of
 * its width (0 = the window's start, 100 = now).
 *
 * Stamps outside the window are DROPPED rather than clamped to the edge: a
 * pile-up at x=0 would read as a burst of trips at the start of the day that
 * never happened. Ordering is preserved from the caller's (already sorted)
 * list.
 */
export function tickPositions(ticks: readonly number[], nowMs: number, windowMs: number): number[] {
  if (!Number.isFinite(windowMs) || windowMs <= 0) return [];
  const from = nowMs - windowMs;
  const out: number[] = [];
  for (const tick of ticks) {
    if (!Number.isFinite(tick)) continue;
    if (tick < from || tick > nowMs) continue;
    out.push(Math.round(((tick - from) / windowMs) * 10000) / 100);
  }
  return out;
}

// ---------------------------------------------------------------------------
// The clear phrase.
// ---------------------------------------------------------------------------

/**
 * `clears above 12.1 V` — what has to happen for this alarm to stand down.
 *
 * `null` unless the compiled rule carries a real `clearpoint` for a threshold
 * class. Hysteresis is the whole point of the sentence: a Low alarm clears
 * ABOVE its clearpoint and a High alarm BELOW it, so the direction is inverted
 * from the trigger's. Pass the trigger already restated in the row's
 * vocabulary (`triggerInVocabulary`) and the same unit the reading wears, or
 * the two halves of the row disagree.
 *
 * `category` + `units` convert the clearpoint into the account's units exactly
 * as the set-point phrase beside it does (ADR 0189). Pass BOTH phrases the same
 * pair: a row saying "alarms below 9.8 ft · clears above 4.5 m" is the failure
 * this argument exists to prevent. With neither, the stored number prints.
 */
export function clearsPhrase(
  trigger: AlarmWireTrigger | null | undefined,
  unit: string | null | undefined,
  category?: UnitCategory,
  units?: UnitSettings
): string | null {
  const clearpoint = trigger?.clearpoint;
  if (clearpoint === undefined || !Number.isFinite(clearpoint)) return null;
  // A clear point that rounds to the set point's text gets a decimal more
  // (TOM-860), as `liveValueLine` prints the set point beside it.
  const minDecimals = distinctDecimals(
    [trigger?.setpoint, clearpoint],
    unit,
    category,
    units
  )(clearpoint);
  const point = formatThreshold(clearpoint, unit ?? null, category, units, { minDecimals });
  if (trigger?.class === 'Low') return `clears above ${point}`;
  if (trigger?.class === 'High') return `clears below ${point}`;
  return null;
}

// ---------------------------------------------------------------------------
// Series rescaling.
// ---------------------------------------------------------------------------

/**
 * Restate a series in the row's vocabulary.
 *
 * The `/telemetry/range` read comes back in STORE units — the same domain as
 * `trigger.setpoint` — so a row whose LABEL was rescaled into the recipe's
 * authored domain (hPa → kPa, `unitVocabulary({rescale:true})`) has to move the
 * trace by the identical factor or the sparkline contradicts the caption above
 * it. `factor === 1` returns the input untouched.
 */
export function scaleSeries(points: readonly SeriesPoint[], factor: number): SeriesPoint[] {
  if (factor === 1 || !Number.isFinite(factor)) return [...points];
  return points.map((p) => ({ t: p.t, v: p.v * factor }));
}

/**
 * Restate a series in the ACCOUNT's units (ADR 0189) — the counterpart of
 * {@link scaleSeries} for a row whose caption this PR converts.
 *
 * A separate function because a factor cannot express every conversion:
 * Celsius to Fahrenheit is affine (×1.8 + 32), so a temperature trace scaled by
 * a factor would sit at the wrong height with the right shape — a sparkline
 * that silently disagrees with the set-point line drawn across it. Each point
 * goes through the same `convertQuantity` the caption does.
 *
 * Passthrough when the caller names no category, holds no settings, or the
 * account's unit for that category IS the store unit — and `scaleSeries` still
 * owns the kPa rescale, which is a vocabulary change, not a unit preference.
 */
export function convertSeries(
  points: readonly SeriesPoint[],
  storeUnit: string | null | undefined,
  category: UnitCategory | undefined,
  units: UnitSettings | undefined
): SeriesPoint[] {
  if (category === undefined || units === undefined) return [...points];
  return points.map((p) => ({
    t: p.t,
    v: Number.isFinite(p.v) ? convertQuantity(p.v, storeUnit, category, units).value : p.v,
  }));
}

// ---------------------------------------------------------------------------
// Sub-lines.
// ---------------------------------------------------------------------------

/**
 * Join a row's supporting facts into one ` · ` line — signal label, the device
 * or channel it came from, which occurrence this is.
 *
 * Blank and repeated parts are dropped, because the catalog routinely names a
 * channel and its device the same thing and "Bilge · Bilge" is noise. `null`
 * when nothing survives, so the caller renders no element at all rather than an
 * empty one.
 */
export function joinParts(parts: readonly (string | null | undefined)[]): string | null {
  const kept: string[] = [];
  for (const part of parts) {
    const text = part?.trim();
    if (text === undefined || text.length === 0) continue;
    if (kept.some((k) => k.toLowerCase() === text.toLowerCase())) continue;
    kept.push(text);
  }
  return kept.length === 0 ? null : kept.join(' · ');
}

/**
 * The safety-off tile's second line: the alarms an owner switched off, named,
 * with a tail once the list would run past the tile.
 *
 * Naming them is the point — "3 safety alarms off" is a number, and the owner
 * needs to know WHICH before deciding it was deliberate.
 */
export function nameList(names: readonly string[], max = 3): string | null {
  const kept = names.map((n) => n.trim()).filter((n) => n.length > 0);
  if (kept.length === 0) return null;
  if (kept.length <= max) return kept.join(' · ');
  const rest = kept.length - max;
  return `${kept.slice(0, max).join(' · ')} · and ${rest} more`;
}
